import { useContext, useMemo, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { BusinessCollectionCompType } from '../../../../../Types/ObjektVy';
import { collectionCompToObjektVy } from '../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionUtils';
import {
  LeverantorQuery_node_TEDLRLeverantor as leverantor_node,
  LeverantorQuery as leverantor,
  LeverantorQueryVariables as leverantorVariables,
} from './../../../Leverantor/ObjektVyer/Leverantor/_QueryTypes/LeverantorQuery';
import { useCollectionMutation } from '../../../../../Components/QueryComponents/CollectionMutation/useCollectionMutation';
import UpdateMutation from '../../../Leverantor/ObjektVyer/Leverantor/LeverantorUpdateMutation.gql';
import AddMutation from '../../../Leverantor/ObjektVyer/Leverantor/LeverantorAddMutation.gql';
import LeverantorQuery from '../../../Leverantor/ObjektVyer/Leverantor/LeverantorQuery.gql';
import {
  LeverantorUpdateMutation as IUpdateData,
  LeverantorUpdateMutationVariables as IUpdateVariables,
} from './../../../Leverantor/ObjektVyer/Leverantor/_QueryTypes/LeverantorUpdateMutation';
import {
  LeverantorAddMutation as IAddData,
  LeverantorAddMutationVariables as IAddVariables,
} from '../../../Leverantor/ObjektVyer/Leverantor/_QueryTypes/LeverantorAddMutation';
import BusinessDataForm, {
  BusinessDataFormContext,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import clientAvenyModel from '../../../../../Model/clientAvenyModel';
import ObjektVySida from '../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import { ObjektVyKort } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import Attribut from '../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import ObjektVy from '../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import {
  AssociationList,
  IAssociationListConfig,
} from '../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import AttributGrupp from '../../../_Common/Components/BusinessCollectionComponents/AttributGrupp/AttributGrupp';
import {
  removeIdsFromNewAssociations,
  IFormData,
  IFieldValidations,
  IValidateFn,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import UtbetalningssattQuery from '../../../Leverantor/ObjektVyer/Leverantor/UtbetalningssattQuery.gql';
import {
  UtbetalningssattQuery as UtbetalningssattQueryData,
  UtbetalningssattQueryVariables,
} from './../../../Leverantor/ObjektVyer/Leverantor/_QueryTypes/UtbetalningssattQuery';
import { leverantorFormStateActionTransformer } from '../../../Leverantor/ObjektVyer/Leverantor/Utils/leverantorUtils';
import {
  TEDLRLeverantorSTATUSEnum,
  TEDOPOrgPersLokTypEnum,
  TEDOPOrgPersLokLOKALADREnum,
  TEDOPOrgPersLokREGELEnum,
  TEDInputLRLeverantorAddValues,
  TEDSelectionConditionType,
  TEDSelectionOperator,
} from '../../../../../globalQueryTypes';
import { head, mergeDeepRight } from 'ramda';
import { emptyUrvalNoPages } from '../../../../../Components/Urval/Utils/urvalUtils';
import { DeepPartial } from '../../../../../Types/UtilTypes';
import { definedSelectedId } from '../../../../../Components/MasterDetail/MasterDetailUtils';
import { useSelectedDetailQuery } from '../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';
import { ICustomValidValues } from '../../../../../Types/ColumnDefType';
import { convertTEDUrvalToUIUrval } from '../../../../../Components/Urval/Utils/convertTEDUrvalToUIUrval';
import { collectionMap } from '../../../../../Model/ModelContext/ModelContext';
import { useActiveDBCollectionQuery } from '../../../../../Components/QueryComponents/ActiveDBCollectionQuery/ActiveDBCollectionQuery';

const leverantormodel: IClientBusinessModel =
  clientAvenyModel.LR.BusinessModels.LRLeverantor;

//Endast i demo, kontonummer som slutar på 2 får välja den hemliga banken
//För övriga konton sorterar vi bort irländska banker.
//I frågan som hämtar defaultvalues så hämtar vi bara banker med postnummer
const validBanks: ICustomValidValues = {
  skipDefaultValues: false,
  validValuesFunction: (defaultValues, formState) => {
    return formState?.currentValues?.['KONTO_NR']?.endsWith('2')
      ? [{ name: 'secret bank', value: head(defaultValues)?.value ?? 'sorry' }]
      : defaultValues.filter(
          option => option.data?._Associations?.Land?.LANDKOD !== 'IE'
        );
  },
  dataDependencies: ['Land.LANDKOD'],
  urval: convertTEDUrvalToUIUrval(
    'SYSBank',
    [
      [
        {
          namn: 'POSTNUMMER',
          typ: TEDSelectionConditionType.Value,
          valueCondition: { op: TEDSelectionOperator.hasvalue },
        },
      ],
    ],
    collectionMap
  ).unsafelyUnwrap(),
};
const levBetalningssattConfig: IAssociationListConfig = {
  inCard: true,
  integrated: true,
  defaultVisningstyp: 'tabell',
  customDefaultColumns: [
    { attributeDef: 'IDString', caption: 'Utbetalningssätt', header: true },
    { attributeDef: 'KONTO_NR' },
    { attributeDef: 'NUMERISKA_FAKTURANR' },
    { attributeDef: 'OCR' },
    {
      association: 'Bank',
      attributeDef: '',
      customValidValues: validBanks,
    },
  ],
  customBusinessConfig: { associations: { Bank: { disableArrowIcon: true } } },
};

const validateSparrdatum: IValidateFn<leverantor_node> = ({
  currentValues,
}): IFieldValidations => {
  const errorMessages: IFieldValidations = [];
  const regDatum = Date.parse(currentValues.REGDATUM);
  const sparrDatum = Date.parse(currentValues.SPARRAD_FROM_DATUM);

  if (sparrDatum && regDatum && sparrDatum < regDatum) {
    errorMessages.push({
      fieldName: 'SPARRAD_FROM_DATUM',
      message: 'Leverantör kan inte spärras innan registreringsdatum',
      type: 'Error',
    });
  }

  return errorMessages;
};

// värden som alltid ska förslås
const baseInitialValue: DeepPartial<TEDInputLRLeverantorAddValues> = {
  STATUS: TEDLRLeverantorSTATUSEnum.NORMAL,
  _Associations: {
    OrgPers_Values: {
      _typ: TEDOPOrgPersLokTypEnum.OVRIGORG,
      REGEL: TEDOPOrgPersLokREGELEnum.STANDARD,
      LOKALADR: TEDOPOrgPersLokLOKALADREnum.LOKAL,
      _Associations: {
        Adress_Values: {},
      },
    },
  },
};

const Leverantor: BusinessCollectionCompType = props => {
  const id = props.id ? props.id : '';
  const {
    nodeData,
    loading: nodeLoading,
    refetch: nodeRefetch,
  } = useSelectedDetailQuery<leverantor, leverantor_node, leverantorVariables>(
    LeverantorQuery,
    props.id
  );

  const [mutation, { loading, error }] = useCollectionMutation<
    IUpdateData,
    IUpdateVariables
  >(UpdateMutation);

  const [addMutation, addStatus] = useMutation<IAddData, IAddVariables>(
    AddMutation
  );

  const { activeDatabase } = useContext(ActiveDatabaseContext);

  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values);
      return mutation({
        variables: {
          db: activeDatabase || '',
          values: {
            id: definedSelectedId(id),
            _version: nodeData ? nodeData._version : 1,
            ...newValues,
          },
        },
      });
    },
    [mutation, activeDatabase, id, nodeData]
  );

  const addFunction = useCallback(
    (values: IFormData) => {
      return addMutation({
        variables: {
          db: activeDatabase || '',
          values: values as TEDInputLRLeverantorAddValues,
        },
      });
    },
    [addMutation, activeDatabase]
  );

  const { data: utbetalningssattData } = useQuery<
    UtbetalningssattQueryData,
    UtbetalningssattQueryVariables
  >(UtbetalningssattQuery, {
    variables: { databaseName: activeDatabase || '' },
    skip: !activeDatabase,
  });

  //hämta leverantörskategorier och föreslå kategori om det bara finns en
  const { data: levkategoridata } = useActiveDBCollectionQuery({
    collectionName: 'LRLeverantorkat',
    attributeDefs: ['id'],
    urval: emptyUrvalNoPages,
  });
  const defaultLevKat =
    levkategoridata != null && levkategoridata.length === 1
      ? levkategoridata.at(0)?.id
      : null;

  const initialValue: IFormData = useMemo(
    () =>
      mergeDeepRight(baseInitialValue, {
        _Associations: { Leverantorskategori_Ref: defaultLevKat },
      }),
    [defaultLevKat]
  );

  return (
    <BusinessDataForm<leverantor_node>
      isNew={props.isNew}
      data={nodeData}
      model={leverantormodel}
      updateMutation={updateFunction}
      addMutation={addFunction}
      fullScreen={props.config?.fullPage ?? false}
      mutationError={error || addStatus.error}
      mutationLoading={loading || addStatus.loading}
      formStateActionTransformer={leverantorFormStateActionTransformer(
        utbetalningssattData?.db?.SYSUtbetalningssatt_All || [],
        nodeData?.OPTyp ?? null
      )}
      newFormContext
      initialValue={initialValue}
      validateFns={[validateSparrdatum as IValidateFn]}
      refetchData={nodeRefetch}
      dataLoading={nodeLoading}
      trace="leverantör"
    >
      <BusinessDataFormContext.Consumer>
        {_businessDataFormContext => (
          <ObjektVy nodeLoading={nodeLoading} editImplemented>
            <ObjektVySida
              sidNr={1}
              caption="Leverantör"
              collectionName={leverantormodel.CollectionName}
            >
              <AttributGrupp>
                <Attribut<leverantor_node> name="LEVERANTORSNR" />
              </AttributGrupp>
              <ObjektVyKort caption="Betalningsinformation">
                <AssociationList
                  associationName="LevBetalningssatt"
                  associationPath="LevBetalningssatt"
                  collectionName={leverantormodel.CollectionName}
                  ownId={definedSelectedId(id)}
                  config={levBetalningssattConfig}
                />
              </ObjektVyKort>
            </ObjektVySida>
          </ObjektVy>
        )}
      </BusinessDataFormContext.Consumer>
    </BusinessDataForm>
  );
};

export const demoRutinTablePerfTest = collectionCompToObjektVy(
  Leverantor,
  'demoRutinTablePerfTest'
);
